import React, { useState } from 'react';
import { List, Tag, Typography, Space, Collapse, Empty, Spin, Button } from 'antd';
import {
  DollarOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  ShoppingCartOutlined,
  CoffeeOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useResponsive } from '../../hooks/useResponsive';
import { useCurrency } from '../../hooks/useCurrency';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { useGetCashMovementsQuery } from '../../services/cashRegisterApi';
import { CashMovementDto } from '../../types/cashRegister';
import { useHubEvent } from '../../hooks/useHubEvent';
import { CASH_MOVEMENTS_CHANGED_EVENT, RECONNECTED_EVENT } from '../../services/realtimeHub';

const { Text } = Typography;
const { Panel } = Collapse;

interface CashMovementsListProps {
  cashRegisterId: string;
  /**
   * 'collapse' (default): the classic collapsible panel, still used inside modals
   * (AdminAllCashRegistersView). 'card': always-open panel for the Mi Caja right
   * column — header with live count, internal scroll, equal-height friendly.
   */
  variant?: 'collapse' | 'card';
}

const CashMovementsList: React.FC<CashMovementsListProps> = ({ cashRegisterId, variant = 'collapse' }) => {
  const { isMobile } = useResponsive();
  const { currencySymbol } = useCurrency();
  const { formatInTimeZone } = useRestaurantTimeZone();
  const [isExpanded, setIsExpanded] = useState(false);

  // Tiempo real: el evento SignalR CashMovementsChanged refetchea al instante;
  // el polling queda como fallback lento por si el websocket se cae.
  const { data: movements, isLoading, refetch, isFetching } = useGetCashMovementsQuery(cashRegisterId, {
    pollingInterval: 60000,
    refetchOnMountOrArgChange: true,
  });

  useHubEvent(CASH_MOVEMENTS_CHANGED_EVENT, (payload) => {
    // El evento trae el cashRegisterId afectado; solo refetchea si es esta caja
    if (String(payload?.cashRegisterId).toLowerCase() === cashRegisterId.toLowerCase()) refetch();
  });
  useHubEvent(RECONNECTED_EVENT, () => refetch());

  const getMovementIcon = (type: string) => {
    switch (type) {
      case 'SALE':
        return <ShoppingCartOutlined style={{ color: '#52c41a' }} />;
      case 'CARD_SALE':
      case 'CREDIT_CARD_SALE':
        return <ShoppingCartOutlined style={{ color: '#1890ff' }} />;
      case 'DEBIT_CARD_SALE':
        return <ShoppingCartOutlined style={{ color: '#13c2c2' }} />;
      case 'TRANSFER_SALE':
        return <ShoppingCartOutlined style={{ color: '#722ed1' }} />;
      case 'OTHER_SALE':
        return <ShoppingCartOutlined style={{ color: '#fa8c16' }} />;
      case 'TIP':
        return <CoffeeOutlined style={{ color: '#1890ff' }} />;
      case 'CORRECTION':
        return <PlusCircleOutlined style={{ color: '#52c41a' }} />;
      case 'DROP':
      case 'PAYOUT':
      case 'EXPENSE':
        return <MinusCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <DollarOutlined />;
    }
  };

  const getMovementColor = (type: string) => {
    switch (type) {
      case 'SALE':
      case 'CORRECTION':
        return 'success';
      case 'CARD_SALE':
      case 'CREDIT_CARD_SALE':
        return 'processing';
      case 'DEBIT_CARD_SALE':
        return 'cyan';
      case 'TRANSFER_SALE':
        return 'purple';
      case 'OTHER_SALE':
        return 'orange';
      case 'TIP':
        return 'cyan';
      case 'DROP':
      case 'PAYOUT':
      case 'EXPENSE':
        return 'error';
      case 'INITIAL':
        return 'default';
      default:
        return 'default';
    }
  };

  const getMovementLabel = (type: string) => {
    switch (type) {
      case 'SALE':
        return 'Venta Efectivo';
      case 'CARD_SALE':
        return 'Venta Tarjeta';
      case 'CREDIT_CARD_SALE':
        return 'Venta T. Crédito';
      case 'DEBIT_CARD_SALE':
        return 'Venta T. Débito';
      case 'TRANSFER_SALE':
        return 'Venta Transferencia';
      case 'OTHER_SALE':
        return 'Venta Otro';
      case 'TIP':
        return 'Propina';
      case 'CORRECTION':
        return 'Ingreso';
      case 'DROP':
        return 'Retiro';
      case 'PAYOUT':
        return 'Pago';
      case 'EXPENSE':
        return 'Gasto';
      case 'INITIAL':
        return 'Inicial';
      default:
        return type;
    }
  };

  const isIncome = (type: string) => {
    // Only physical cash income should show as positive
    return ['SALE', 'TIP', 'CORRECTION', 'INITIAL'].includes(type);
  };

  const isNonCashSale = (type: string) => {
    // Card and transfer sales are recorded but don't affect cash count
    return ['CARD_SALE', 'CREDIT_CARD_SALE', 'DEBIT_CARD_SALE', 'TRANSFER_SALE', 'OTHER_SALE'].includes(type);
  };

  const formatTime = (timestamp: string) => formatInTimeZone(timestamp, 'HH:mm:ss');

  // Machine-generated references ("ORDER-{guid}") are audit keys, not user info — hide
  // them. Hand-typed references on manual movements still show.
  const isAutoReference = (reference: string) =>
    /^ORDER-[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(reference);

  const sortedMovements = movements
    ? [...movements].sort((a, b) =>
        dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf()
      )
    : [];

  const listBody = isLoading ? (
    <div style={{ textAlign: 'center', padding: '20px' }}>
      <Spin />
    </div>
  ) : sortedMovements.length === 0 ? (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description="No hay movimientos registrados"
      style={{ padding: '20px 0' }}
    />
  ) : (
    <List
      size="small"
      dataSource={sortedMovements}
      style={variant === 'card' ? undefined : {
        maxHeight: isMobile ? '300px' : '400px',
        overflowY: 'auto',
      }}
      renderItem={(movement: CashMovementDto) => (
        <List.Item
          style={{
            padding: isMobile ? '8px 0' : '12px 0',
            borderBottom: '1px solid var(--border-color)',
          }}
        >
          <div style={{ width: '100%' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: 4,
              }}
            >
              <Space size={4}>
                {getMovementIcon(movement.type)}
                <Tag color={getMovementColor(movement.type)} style={{ margin: 0 }}>
                  {getMovementLabel(movement.type)}
                </Tag>
              </Space>
              <Text
                strong
                style={{
                  fontSize: isMobile ? '14px' : '16px',
                  fontVariantNumeric: 'tabular-nums',
                  color: isIncome(movement.type)
                    ? '#52c41a'
                    : isNonCashSale(movement.type)
                    ? '#1890ff'
                    : '#ff4d4f',
                }}
              >
                {isIncome(movement.type) ? '+' : isNonCashSale(movement.type) ? '' : '-'}
                {currencySymbol} {movement.amount.toFixed(2)}
              </Text>
            </div>

            <div style={{ paddingLeft: isMobile ? 0 : 24 }}>
              <Text
                type="secondary"
                style={{
                  fontSize: isMobile ? '11px' : '12px',
                  display: 'block',
                  marginBottom: 2,
                }}
              >
                {movement.reason}
              </Text>

              {movement.reference && !isAutoReference(movement.reference) && (
                <Text
                  type="secondary"
                  style={{
                    fontSize: isMobile ? '10px' : '11px',
                    display: 'block',
                    fontStyle: 'italic',
                  }}
                >
                  Ref: {movement.reference}
                </Text>
              )}

              {movement.onBehalfOfUserName && (
                <Text
                  type="warning"
                  style={{
                    fontSize: isMobile ? '10px' : '11px',
                    display: 'block',
                    marginTop: 2,
                    fontWeight: 500,
                  }}
                >
                  👤 Registrado por {movement.createdByUserName} en nombre de {movement.onBehalfOfUserName}
                </Text>
              )}

              <Text
                type="secondary"
                style={{
                  fontSize: isMobile ? '10px' : '11px',
                  display: 'block',
                  marginTop: 4,
                }}
              >
                <ClockCircleOutlined style={{ marginRight: 4, fontSize: '10px' }} />
                {formatTime(movement.createdAt)}
              </Text>
            </div>
          </div>
        </List.Item>
      )}
    />
  );

  if (variant === 'card') {
    return (
      <div className="cash-panel">
        <div className="cash-panel__title">
          <span>
            Movimientos del día · {movements?.length || 0}
            {isFetching && <Spin size="small" style={{ marginLeft: 8 }} />}
          </span>
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined spin={isFetching} />}
            onClick={() => refetch()}
          />
        </div>
        {/* Absolute-fill: the list contributes zero intrinsic height, so the grid
            row is sized by the summary column and the overflow scrolls here. */}
        <div className="cash-movements__body">
          <div className="cash-movements__scroll">
            {listBody}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Collapse
      ghost
      onChange={(keys) => setIsExpanded(keys.length > 0)}
      style={{ marginTop: 16 }}
    >
      <Panel
        header={
          <Space>
            <ClockCircleOutlined />
            <Text strong>Movimientos del Día ({movements?.length || 0})</Text>
            {isFetching && <Spin size="small" />}
          </Space>
        }
        key="1"
        extra={
          isExpanded && (
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined spin={isFetching} />}
              onClick={(e) => {
                e.stopPropagation();
                refetch();
              }}
            />
          )
        }
      >
        {listBody}
      </Panel>
    </Collapse>
  );
};

export default CashMovementsList;
